'use client';

import { ArrowLeft, Camera as CameraIcon, FlipHorizontal } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Modal, { ModalContent } from '../HOC/Modal';
import Button from './Button';
type Props = {
  closeHandler: () => void;
  setNewPhotoFcn: (photo: {
    uri: string;
    file?: File;
  }) => void;
};
export const Camera = (props: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Ref, not state: the cleanup below must see the stream that is live *now*,
  // and nothing renders off it. As state it would close over the previous
  // render's stream and leave the newest camera track running.
  const streamRef = useRef<MediaStream | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [photo, setPhoto] = useState<{
    uri: string;
    file?: File;
  } | undefined>();
  const [facing, setFacing] = useState<'user' | 'environment'>('environment');
  const [error, setError] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  useEffect(() => {
    let ignore = false;
    const init = async () => {
      await detectCameras(() => ignore);
      if (ignore) return;
      await startCamera(() => ignore);
    };
    void init();
    return () => {
      ignore = true;
      stopTracks();
    };
  }, [facing, photo]);
  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };
  const detectCameras = async (isStale: () => boolean = () => false) => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasMultipleCameras(false);
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (isStale()) return;
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch (err) {
      console.error('Error detecting cameras:', err);
      if (isStale()) return;
      setHasMultipleCameras(false);
    }
  };
  const startCamera = async (isStale_0: () => boolean = () => false) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not available. Please use HTTPS or a secure context.');
      return;
    }
    try {
      stopTracks();
      const constraints = {
        video: {
          facingMode: facing,
          width: {
            ideal: 1920
          },
          height: {
            ideal: 1080
          }
        },
        audio: false
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      if (isStale_0()) {
        // Facing/photo changed while getUserMedia was in flight — this stream
        // is already orphaned, so release it instead of wiring it up.
        mediaStream.getTracks().forEach(track_0 => track_0.stop());
        return;
      }
      streamRef.current = mediaStream;
      setError(null);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err_0) {
      console.error('Error accessing camera:', err_0);
      if (isStale_0()) return;
      setError('Unable to access camera. Please check permissions.');
    }
  };
  const takePicture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (context) {
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (!blob) return;
          const file = new File([blob], 'camera-photo.jpg', {
            type: 'image/jpeg'
          });
          const uri = URL.createObjectURL(file);
          setPhoto({
            uri,
            file
          });
          setPreviewVisible(true);
        }, 'image/jpeg', 0.5);
      }
    }
  };
  const flipCamera = () => {
    setFacing(prev => prev === 'user' ? 'environment' : 'user');
  };
  const retakePicture = () => {
    setPreviewVisible(false);
    setPhoto(undefined);
  };
  const handleClose = () => {
    stopTracks();
    props.closeHandler();
  };
  const savePicture = () => {
    if (photo) {
      props.setNewPhotoFcn(photo);
      handleClose();
    }
  };
  if (error) {
    return <Modal closeHandler={handleClose}>
        <ModalContent>
          <div className="p-6 text-center">
            <h3 className="mb-4 text-lg font-semibold">Camera Error</h3>
            <p className="mb-4 text-textSecondary">{error}</p>
            <Button text="Try Again" onClick={() => void startCamera()} variant="PRIMARY" />
          </div>
        </ModalContent>
      </Modal>;
  }
  return <Modal closeHandler={handleClose}>
      <div className="relative mx-auto h-[80vh] w-full max-w-2xl overflow-hidden rounded-lg bg-black">
        {previewVisible && photo ? <div className="relative size-full">
            <button className="absolute left-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70" onClick={handleClose}>
              <ArrowLeft size={24} />
            </button>
            <img src={photo.uri} alt="Preview" className="size-full object-contain" />
            <div className="absolute inset-x-0 bottom-4 flex justify-center gap-4">
              <Button text="Retake" onClick={retakePicture} variant="SECONDARY" classNames="w-24" />
              <Button text="Save" onClick={savePicture} variant="PRIMARY" classNames="w-24" />
            </div>
          </div> : <div className="relative flex size-full flex-col items-center justify-center">
            <video ref={videoRef} autoPlay playsInline muted className="h-auto w-full" />
            <canvas ref={canvasRef} style={{
          display: 'none'
        }} />

            {/* Camera Controls */}
            <div className="absolute inset-0 flex flex-col justify-between p-4">
              {/* Top Bar */}
              <div className="flex items-center justify-between">
                <button className="rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70" onClick={handleClose}>
                  <ArrowLeft size={24} />
                </button>
              </div>

              {/* Bottom Controls */}
              <div className="flex items-center justify-center gap-8 pb-8">
                <Button variant="CLEAR" onClick={takePicture} icon={<CameraIcon size={32} />} />
                {hasMultipleCameras && <Button variant="CLEAR" onClick={flipCamera} icon={<FlipHorizontal size={24} />} />}
              </div>
            </div>
          </div>}
      </div>
    </Modal>;
};